import { ImgHTMLAttributes, SyntheticEvent, useState } from 'react';
import styled from 'styled-components';

type Props = ImgHTMLAttributes<HTMLImageElement> & {
  /**
   * 로드에 실패했을 때 새 URL 을 받아오라고 알린다(한 번만 호출).
   * 보통 해당 화면의 목록 조회를 다시 실행하도록 연결한다.
   */
  onExpired?: () => void;
};

/**
 * 서명 URL(presigned URL) 로 내려오는 이미지를 그린다.
 *
 * 서명 URL 은 유효기간(전표웹·영수증웹 15분)이 지나면 403 이 된다. 화면을 띄워둔
 * 채 시간이 지나면 이미 늙은 URL 이 남아 있어 이미지가 깨지는데, 새 URL 을 받으려면
 * API 를 다시 호출하는 수밖에 없다. 로드가 실패하면 `onExpired` 로 재조회를 요청해
 * 한 번만 다시 시도하고, 그래도 실패하면 안내 문구로 바꾼다.
 */
export const ExpiringImage = ({
  src,
  alt = '',
  onExpired,
  onError,
  ...rest
}: Props) => {
  const [state, setState] = useState({ src, retryCount: 0, isFailed: false });

  // src 가 바뀌면(= 새 URL 을 받아왔으면) 재시도 기회를 다시 준다.
  const isNewSrc = state.src !== src;
  if (isNewSrc) {
    setState({ src, retryCount: 0, isFailed: false });
  }

  const retryCount = isNewSrc ? 0 : state.retryCount;
  const isFailed = isNewSrc ? false : state.isFailed;

  const handleError = (event: SyntheticEvent<HTMLImageElement>) => {
    onError?.(event);

    if (retryCount > 0 || !onExpired) {
      setState((prev) => ({ ...prev, isFailed: true }));
      return;
    }

    setState((prev) => ({ ...prev, retryCount: 1 }));
    onExpired();
  };

  if (isFailed) {
    return <Fallback {...rest}>이미지를 불러오지 못했습니다.</Fallback>;
  }

  return (
    // 새 URL 이 같은 값으로 와도 다시 로드하도록 key 를 바꿔 준다.
    <img
      key={`${src}-${retryCount}`}
      src={src}
      alt={alt}
      {...rest}
      onError={handleError}
    />
  );
};

const Fallback = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 80px;
  padding: 12px;
  border-radius: 6px;
  background-color: #f5f6f7;
  color: #80848a;
  font-size: 12px;
  line-height: 150%;
  text-align: center;
`;
